import './VillainsDossierDrawer.css';
import type {
  VillainsNpcManifestEntry,
  VillainsNpcCategory,
} from '../../services/host/VillainsServiceModule';
import type {
  VillainsLibraryState,
  VillainsLibraryTab,
} from '../../hooks/useVillainsLibrary';

/**
 * VillainsDossierDrawer — 2026-06-08 §3.E for Villains Supplement VG35011.
 *
 * 3-tab layout (per locked §3.A category-design decision):
 *   - Major Villains (7) — Cartesia / Buonvisi / Vandemere / Parkhurst /
 *     Kleiner / Prosinski / Larose. Full 5-stat blocks + Villains-supplement
 *     extensions (survival_points, idiosyncrasies, interaction_modifiers,
 *     description, concept, organization, fortress, personality, goals).
 *   - Lieutenants (9) — 7 stat-block lieutenants (Cartesia's 3 Thinkers +
 *     Octavia's 2 + Larose's 2) + 2 Vandemere prose-only lieutenants
 *     (Van Esterfeld + Golder). Stat-block lieutenants render full dossier;
 *     prose-only show concept + role + background only.
 *   - SMERSH (10) — 4 leaders (Lebedev / Shadrin / Kreniak / Skoda) with
 *     simplified WIL+PER stat blocks + 6 agents (Fedisov / Pimberton /
 *     Valentine / Morgen / Malak / Castenada) with full 5-stat blocks.
 *
 * Banner surfaces honest authoring progress per anti-enshittification rule:
 * pending = 3 (Branded Man trio deferred to §3.J).
 */

const TAB_DEFS: ReadonlyArray<{ key: VillainsLibraryTab; label: string }> = [
  { key: 'major_villain', label: 'Major Villains' },
  { key: 'lieutenant', label: 'Lieutenants' },
  { key: 'smersh', label: 'SMERSH' },
];

const TAB_TO_CATEGORIES: Record<
  VillainsLibraryTab,
  ReadonlySet<VillainsNpcCategory>
> = {
  major_villain: new Set<VillainsNpcCategory>(['major_villain']),
  lieutenant: new Set<VillainsNpcCategory>([
    'major_villain_lieutenant',
    'major_villain_lieutenant_prose_only',
  ]),
  smersh: new Set<VillainsNpcCategory>(['smersh_leader', 'smersh_agent']),
};

const STAT_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'STR', label: 'STR' },
  { key: 'DEX', label: 'DEX' },
  { key: 'WIL', label: 'WIL' },
  { key: 'PER', label: 'PER' },
  { key: 'INT', label: 'INT' },
];

const PHYSICAL_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'height', label: 'Height' },
  { key: 'weight', label: 'Weight' },
  { key: 'age', label: 'Age' },
  { key: 'appearance', label: 'Appearance' },
  { key: 'hand_to_hand_damage_class', label: 'H2H DC' },
  { key: 'stamina', label: 'Stamina' },
  { key: 'running_swimming', label: 'Run/Swim' },
  { key: 'carrying', label: 'Carrying' },
  { key: 'speed', label: 'Speed' },
  { key: 'weapon', label: 'Weapon' },
];

const INTERACTION_ORDER: ReadonlyArray<string> = [
  'Reaction',
  'Persuasion',
  'Seduction',
  'Interrogation',
  'Torture',
];

const DESCRIPTION_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'hair', label: 'Hair' },
  { key: 'eyes', label: 'Eyes' },
  { key: 'scars', label: 'Scars' },
  { key: 'distinguishing_marks', label: 'Distinguishing Marks' },
];

export interface VillainsDossierDrawerProps {
  npcs: VillainsNpcManifestEntry[];
  pendingRemainingCount: number;
  state: VillainsLibraryState;
  error?: string | null;
  activeTab: VillainsLibraryTab;
  onTabChange: (tab: VillainsLibraryTab) => void;
  selectedNpcId: string | null;
  selectedNpcData: Record<string, unknown> | null;
  selectedNpcState: VillainsLibraryState;
  onSelectNpc: (id: string) => void;
  onClose?: () => void;
  testidPrefix?: string;
}

function formatModifier(n: number): string {
  return n > 0 ? `+${n}` : `${n}`;
}

function VillainDetail({
  data,
  testidPrefix,
}: {
  data: Record<string, unknown>;
  testidPrefix: string;
}): JSX.Element {
  const name = data['name'] as string | undefined;
  const manifestSection = data['manifest_section'] as string | undefined;
  const sourceMdLine = data['source_md_line'] as number | undefined;
  const category = data['category'] as VillainsNpcCategory | undefined;
  const stats = (data['stats'] ?? {}) as Record<string, number | null>;
  const skills = (data['skills'] ?? {}) as Record<
    string,
    { skill_level: number; primary_chance: number }
  >;
  const abilities = (data['abilities'] ?? []) as string[];
  const physical = (data['physical'] ?? {}) as Record<string, unknown>;
  const fields = (data['fields_of_experience'] ?? []) as string[];
  const weaknesses = (data['weaknesses'] ?? []) as string[];
  const famePoints = data['fame_points'] as number | null | undefined;
  const famePointsPublicCover = data['fame_points_public_cover'] as
    | number
    | null
    | undefined;
  const survivalPoints = data['survival_points'] as number | null | undefined;
  const idiosyncrasies = data['idiosyncrasies'] as string | null | undefined;
  const interactionModifiers = (data['interaction_modifiers'] ?? null) as
    | Record<string, number>
    | null;
  const description = (data['description'] ?? null) as
    | Record<string, string>
    | null;
  const concept = data['concept'] as string | null | undefined;
  const organization = data['organization'] as string | null | undefined;
  const fortress = data['fortress'] as string | null | undefined;
  const background = data['background'] as string | null | undefined;
  const personality = data['personality'] as string | null | undefined;
  const goals = data['goals'] as string | null | undefined;
  const gameInformation = data['game_information'] as string | null | undefined;

  const associatedVillainId = data['associated_villain_id'] as
    | string
    | null
    | undefined;
  const roleUnderVillain = data['role_under_villain'] as
    | string
    | null
    | undefined;

  const smershDirectorate = data['smersh_directorate'] as string | undefined;
  const smershOfficialActive = data['smersh_official_active'] as
    | boolean
    | undefined;
  const rankNote = data['rank_note'] as string | undefined;
  const modusOperandi = data['modus_operandi'] as string | undefined;
  const signatureWeapon = data['signature_weapon'] as string | undefined;

  const isProseOnly = category === 'major_villain_lieutenant_prose_only';
  const isLeader = category === 'smersh_leader';
  const isAgent = category === 'smersh_agent';

  const hasAnyStats = STAT_ORDER.some(
    (s) => stats[s.key] !== null && stats[s.key] !== undefined,
  );

  return (
    <div
      className="br-villains-npc-detail"
      data-testid={`${testidPrefix}-detail`}
    >
      <div className="br-villains-npc-detail-header">
        <div className="br-villains-npc-detail-name br-display">{name}</div>
        <div className="br-villains-npc-detail-citation br-label">
          VG35011 §{manifestSection}
          {sourceMdLine ? ` · MD L${sourceMdLine}` : ''}
        </div>
      </div>

      {concept && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">Concept</div>
          <p
            className="br-villains-npc-detail-prose"
            data-testid={`${testidPrefix}-detail-concept`}
          >
            {concept}
          </p>
        </div>
      )}

      {associatedVillainId && (
        <div
          className="br-villains-npc-detail-section br-villains-npc-detail-association"
          data-testid={`${testidPrefix}-detail-association`}
        >
          <div className="br-villains-npc-detail-section-label">
            Serves Under
          </div>
          <p className="br-villains-npc-detail-prose">
            <span className="br-label">{associatedVillainId}</span>
            {roleUnderVillain ? ` — ${roleUnderVillain}` : ''}
          </p>
        </div>
      )}

      {smershDirectorate && (
        <div
          className="br-villains-npc-detail-section br-villains-npc-detail-smersh-directorate"
          data-testid={`${testidPrefix}-detail-smersh-directorate`}
        >
          <div className="br-villains-npc-detail-section-label">
            SMERSH Directorate
          </div>
          <p className="br-villains-npc-detail-prose">{smershDirectorate}</p>
        </div>
      )}

      {isAgent && smershOfficialActive !== undefined && (
        <div
          className="br-villains-npc-detail-section br-villains-npc-detail-smersh-status"
          data-testid={`${testidPrefix}-detail-smersh-status`}
        >
          <div className="br-villains-npc-detail-section-label">Status</div>
          <p className="br-villains-npc-detail-prose">
            {smershOfficialActive
              ? 'Officially active (Politburo records)'
              : 'Retainer (off-record, hired-from-outside)'}
          </p>
        </div>
      )}

      {isProseOnly && (
        <div
          className="br-villains-npc-detail-warn"
          data-testid={`${testidPrefix}-detail-prose-only-banner`}
        >
          <span className="br-villains-npc-detail-warn-icon">⚠</span>
          <span className="br-villains-npc-detail-warn-text">
            Prose-only lieutenant — no stat block in source MD; only role +
            background available.
          </span>
        </div>
      )}

      {isLeader && rankNote && (
        <div
          className="br-villains-npc-detail-warn"
          data-testid={`${testidPrefix}-detail-rank-note`}
        >
          <span className="br-villains-npc-detail-warn-icon">ℹ</span>
          <span className="br-villains-npc-detail-warn-text">
            <strong>Rank note:</strong> {rankNote}
          </span>
        </div>
      )}

      {hasAnyStats && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">Stats</div>
          <dl className="br-villains-npc-detail-stats">
            {STAT_ORDER.map((s) => {
              const v = stats[s.key];
              if (v === undefined) return null;
              return (
                <div key={s.key} className="br-villains-npc-detail-stat-tile">
                  <dt>{s.label}</dt>
                  <dd className="br-mono">{v === null ? '—' : v}</dd>
                </div>
              );
            })}
          </dl>
        </div>
      )}

      {Object.keys(skills).length > 0 && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">Skills</div>
          <table className="br-villains-npc-detail-skills">
            <thead>
              <tr>
                <th>Skill</th>
                <th>SL</th>
                <th>PC</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(skills).map(([k, v]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td className="br-mono">{v.skill_level}</td>
                  <td className="br-mono">{v.primary_chance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {abilities.length > 0 && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">Abilities</div>
          <ul className="br-villains-npc-detail-chips">
            {abilities.map((a) => (
              <li key={a} className="br-villains-npc-detail-chip">
                {a}
              </li>
            ))}
          </ul>
        </div>
      )}

      {Object.keys(physical).length > 0 && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">Physical</div>
          <dl className="br-villains-npc-detail-physical">
            {PHYSICAL_ORDER.map((p) =>
              physical[p.key] !== undefined && physical[p.key] !== null ? (
                <div
                  key={p.key}
                  className="br-villains-npc-detail-physical-row"
                >
                  <dt>{p.label}</dt>
                  <dd>{String(physical[p.key])}</dd>
                </div>
              ) : null,
            )}
          </dl>
        </div>
      )}

      <div className="br-villains-npc-detail-section br-villains-npc-detail-points">
        {famePoints !== undefined && famePoints !== null && (
          <div className="br-villains-npc-detail-point-tile">
            <span className="br-villains-npc-detail-point-label">Fame</span>
            <span className="br-villains-npc-detail-point-value br-label">
              {famePoints}
              {famePointsPublicCover !== undefined &&
                famePointsPublicCover !== null && (
                  <span className="br-villains-npc-detail-point-suffix">
                    /{famePointsPublicCover}
                  </span>
                )}
            </span>
          </div>
        )}
        {survivalPoints !== undefined && survivalPoints !== null && (
          <div className="br-villains-npc-detail-point-tile">
            <span className="br-villains-npc-detail-point-label">
              Survival
            </span>
            <span className="br-villains-npc-detail-point-value br-label">
              {survivalPoints}
            </span>
          </div>
        )}
      </div>

      {fields.length > 0 && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Fields of Experience
          </div>
          <ul className="br-villains-npc-detail-chips">
            {fields.map((f) => (
              <li key={f} className="br-villains-npc-detail-chip">
                {f}
              </li>
            ))}
          </ul>
        </div>
      )}

      {weaknesses.length > 0 && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Weaknesses
          </div>
          <ul className="br-villains-npc-detail-chips">
            {weaknesses.map((w) => (
              <li key={w} className="br-villains-npc-detail-chip">
                {w}
              </li>
            ))}
          </ul>
        </div>
      )}

      {idiosyncrasies && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Idiosyncrasies
          </div>
          <p
            className="br-villains-npc-detail-prose"
            data-testid={`${testidPrefix}-detail-idiosyncrasies`}
          >
            {idiosyncrasies}
          </p>
        </div>
      )}

      {interactionModifiers && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Interaction Modifiers
          </div>
          <dl
            className="br-villains-npc-detail-modifiers"
            data-testid={`${testidPrefix}-detail-interaction-modifiers`}
          >
            {INTERACTION_ORDER.map((key) =>
              interactionModifiers[key] !== undefined ? (
                <div
                  key={key}
                  className="br-villains-npc-detail-modifier-row"
                >
                  <dt>{key}</dt>
                  <dd className="br-mono">
                    {formatModifier(interactionModifiers[key])}
                  </dd>
                </div>
              ) : null,
            )}
          </dl>
        </div>
      )}

      {description && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Description
          </div>
          <dl className="br-villains-npc-detail-description">
            {DESCRIPTION_ORDER.map((d) =>
              description[d.key] !== undefined ? (
                <div
                  key={d.key}
                  className="br-villains-npc-detail-description-row"
                >
                  <dt>{d.label}</dt>
                  <dd>{description[d.key]}</dd>
                </div>
              ) : null,
            )}
          </dl>
        </div>
      )}

      {organization && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Organization
          </div>
          <p className="br-villains-npc-detail-prose">{organization}</p>
        </div>
      )}

      {fortress && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">Fortress</div>
          <p className="br-villains-npc-detail-prose">{fortress}</p>
        </div>
      )}

      {modusOperandi && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Modus Operandi
          </div>
          <p
            className="br-villains-npc-detail-prose"
            data-testid={`${testidPrefix}-detail-modus-operandi`}
          >
            {modusOperandi}
          </p>
        </div>
      )}

      {signatureWeapon && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Signature Weapon
          </div>
          <p
            className="br-villains-npc-detail-prose"
            data-testid={`${testidPrefix}-detail-signature-weapon`}
          >
            {signatureWeapon}
          </p>
        </div>
      )}

      {background && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Background
          </div>
          <p
            className="br-villains-npc-detail-prose"
            data-testid={`${testidPrefix}-detail-background`}
          >
            {background}
          </p>
        </div>
      )}

      {personality && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Personality
          </div>
          <p className="br-villains-npc-detail-prose">{personality}</p>
        </div>
      )}

      {goals && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">Goals</div>
          <p className="br-villains-npc-detail-prose">{goals}</p>
        </div>
      )}

      {gameInformation && (
        <div className="br-villains-npc-detail-section">
          <div className="br-villains-npc-detail-section-label">
            Game Information
          </div>
          <p className="br-villains-npc-detail-prose">{gameInformation}</p>
        </div>
      )}
    </div>
  );
}

export function VillainsDossierDrawer({
  npcs,
  pendingRemainingCount,
  state,
  error,
  activeTab,
  onTabChange,
  selectedNpcId,
  selectedNpcData,
  selectedNpcState,
  onSelectNpc,
  onClose,
  testidPrefix = 'br-villains-dossier-library',
}: VillainsDossierDrawerProps): JSX.Element {
  const activeCategories = TAB_TO_CATEGORIES[activeTab];
  const visibleNpcs = npcs.filter((n) => activeCategories.has(n.category));

  return (
    <aside
      className="br-scenario-drawer br-villains-dossier-library-drawer"
      data-testid={testidPrefix}
      data-state={state}
    >
      <header>
        <h2 className="br-display">Villains Dossier</h2>
        {/* 2026-06-09 Bug 1 drift fix — see CanonicalNpcsDrawer for the
         *  full rationale. Mirrors the 7 sibling drawers that already
         *  render-gate this on `onClose !== undefined`. */}
        {onClose !== undefined && (
          <button
            type="button"
            className="br-scenario-drawer-close"
            data-testid={`${testidPrefix}-close`}
            onClick={onClose}
            aria-label="Close Villains Dossier drawer"
          >
            ✕
          </button>
        )}
      </header>

      <p
        className="br-label br-villains-npc-banner"
        data-testid={`${testidPrefix}-banner`}
      >
        Victory Games VG35011 — Villains Supplement (Major Villains booklet
        + SMERSH booklet). <strong>{npcs.length} authored</strong>
        {pendingRemainingCount > 0 ? (
          <>
            {' '}· {pendingRemainingCount} more enumerated as pending in the
            seed file (Branded Man adventure NPCs, deferred to §3.J).
          </>
        ) : (
          <> · 0 pending.</>
        )}
      </p>

      <div
        className="br-scenario-drawer-tabs"
        data-testid={`${testidPrefix}-tabs`}
        role="tablist"
      >
        {TAB_DEFS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            data-testid={`${testidPrefix}-tab-${t.key}`}
            data-active={t.key === activeTab ? 'true' : 'false'}
            aria-selected={t.key === activeTab}
            onClick={() => onTabChange(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {state === 'loading' && (
        <div className="br-label" data-testid={`${testidPrefix}-loading`}>
          Loading Villains dossiers…
        </div>
      )}
      {state === 'error' && (
        <div className="br-label" data-testid={`${testidPrefix}-error`}>
          {error ?? 'Failed to load Villains dossiers.'}
        </div>
      )}
      {state === 'ready' && (
        <>
          <div data-testid={`${testidPrefix}-count`} className="br-label">
            {visibleNpcs.length}
          </div>
          <ul
            className="br-villains-npc-list"
            data-testid={`${testidPrefix}-list`}
          >
            {visibleNpcs.length === 0 && (
              <li
                className="br-label br-villains-npc-empty"
                data-testid={`${testidPrefix}-empty`}
              >
                No NPCs in this category.
              </li>
            )}
            {visibleNpcs.map((n) => (
              <li key={n.id} className="br-villains-npc-row-wrapper">
                <button
                  type="button"
                  className="br-villains-npc-row"
                  data-testid={`${testidPrefix}-row-${n.id}`}
                  data-selected={n.id === selectedNpcId ? 'true' : 'false'}
                  onClick={() => onSelectNpc(n.id)}
                >
                  <span className="br-villains-npc-row-name">{n.name}</span>
                  <span className="br-villains-npc-row-section br-label">
                    §{n.manifest_section}
                  </span>
                </button>
                {n.id === selectedNpcId &&
                  selectedNpcState === 'loading' && (
                    <div
                      className="br-label"
                      data-testid={`${testidPrefix}-detail-loading`}
                    >
                      Loading detail…
                    </div>
                  )}
                {n.id === selectedNpcId &&
                  selectedNpcState === 'ready' &&
                  selectedNpcData && (
                    <VillainDetail
                      data={selectedNpcData}
                      testidPrefix={testidPrefix}
                    />
                  )}
                {n.id === selectedNpcId &&
                  selectedNpcState === 'error' && (
                    <div
                      className="br-label"
                      data-testid={`${testidPrefix}-detail-error`}
                    >
                      Failed to load detail.
                    </div>
                  )}
              </li>
            ))}
          </ul>
        </>
      )}
    </aside>
  );
}
